import { useState, useCallback, useEffect } from 'react'
import styled from '@emotion/styled'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import { motion } from 'framer-motion'
import { useDispatch } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { useMfaStore } from 'login/mfa/store/useMfaStore'
import { ErrorMessage } from './error-message'
import { getSignup, confirmSignup } from '../authed/auth.actions'
import * as SnackbarActions from '../global/snackbar/snackbar.actions'
import { t } from '@lingui/core/macro'

const FloatRight = styled.div`
  float: right;
  padding-top: 12px;
`
const TextFieldMarginTop = styled(TextField)`
  margin-top: 12px;
`

export const Welcome = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const { token } = useParams()
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [name, setName] = useState('')
  const [canSubmit, setCanSubmit] = useState(true)

  const { setMfaRequired } = useMfaStore()

  useEffect(() => {
    getSignup(token).then(({ data }) => {
      if (data.length === 0) {
        // history.push('/auth/login')
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`For security purposes, this invitation has expired. Please reach out to your administrator to request a new invitation.`,
            type: 'ERROR',
          }),
        )
        setCanSubmit(false)
      }
      setUser(data)
      setName(data.name)
    })
  }, [token, history])

  const handleSubmit = useCallback(
    e => {
      e.preventDefault()
      setLoading(true)
      confirmSignup({
        password,
        password_confirmation: confirmPassword,
        token,
        email: user.email,
        name,
      })
        .then(() => {
          setLoading(false)
          history.push('/auth/login')
          dispatch(
            SnackbarActions.showSnackbar({
              message: t`You have finished sign up. Login above`,
              type: 'SUCCESS',
            }),
          )
        })
        .catch(err => {
          if (err?.response?.data?.mfa_required && err?.status === 428) {
            setMfaRequired(true)

            setShowPassword(false)
            history.push(
              `/auth/mfa?email=${encodeURIComponent(user.email)}&mfa_token=${err.response.data.mfa_token}`,
            )
            return
          }

          setLoading(false)
          dispatch(
            SnackbarActions.showSnackbar({
              message: ErrorMessage(err),
              type: 'ERROR',
            }),
          )
        })
    },
    [password, confirmPassword, token, history, name, user, dispatch],
  )

  return (
    <motion.div
      className="ConfirmResetPassword"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      <form onSubmit={handleSubmit}>
        <div>
          <TextFieldMarginTop
            disabled
            label={t`Email`}
            variant="outlined"
            fullWidth
            value={user ? user.email : ''}
          />
          <TextFieldMarginTop
            value={name}
            onChange={({ target: { value } }) => setName(value)}
            label={t`Name`}
            fullWidth
            variant="outlined"
          />
          <TextFieldMarginTop
            variant="outlined"
            value={password}
            onChange={({ target: { value } }) => setPassword(value)}
            type={showPassword ? 'text' : 'password'}
            label={t`Password`}
            fullWidth={true}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowPassword(prev => !prev)
                    }}
                    size="large"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextFieldMarginTop
            variant="outlined"
            value={confirmPassword}
            onChange={({ target: { value } }) => setConfirmPassword(value)}
            type={showConfirm ? 'text' : 'password'}
            label={t`Confirm password`}
            fullWidth={true}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowConfirm(prev => !prev)
                    }}
                    size="large"
                  >
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <FloatRight>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || !canSubmit}
            type="submit"
            disableElevation={true}
          >
            {t`Submit`}
          </Button>
        </FloatRight>
      </form>
    </motion.div>
  )
}
